'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';

/**
 * A Modern Keepsake section - value proposition
 *
 * Simple, clean layout with:
 * - Left: Single lifestyle image (artwork + phone)
 * - Right: Text content with heading and description
 */
export function ValueProp() {
  return (
    <section className="py-16 md:py-24 bg-cream-200">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Lifestyle Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <Image
                src="/marketing/keepsake-lifestyle.png"
                alt="Child's artwork transformed into a digital storybook on a smartphone"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority
              />
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:pl-4"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-charcoal-600 mb-6">
              A Modern Keepsake
            </h2>

            <p className="text-base md:text-lg text-taupe-500 leading-relaxed mb-6">
              Transform your child&apos;s artwork into treasured storybooks.
              Upload their drawings, watch AI bring them to life with personalized
              stories, and create lasting memories your family will cherish forever.
            </p>

            <Link href="/signup">
              <Button
                variant="link"
                className="text-terracotta-500 hover:text-terracotta-600 p-0 font-medium text-base"
              >
                Learn More
                <span className="ml-2">&rarr;</span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
